<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="project-actions-wrapper">
  <rename-modal
    :title="$t('rename-project')"
    :currentName="project.name"
    :active.sync="isRenameModalActive"
    @rename="rename"
  />

  <form @submit.prevent="saveOntology()">
    <ims-modal :active="isOntologyModalActive" :title="$t('change-ontology')" @close="isOntologyModalActive = false">
      <b-message v-if="errorOntologies" type="is-danger" has-icon icon-size="is-small">
        <h2> {{ $t('error') }} </h2>
        <p> {{ $t('unexpected-error-info-message') }} </p>
      </b-message>
      <template v-else>
        <b-message v-if="project.ontology" type="is-warning" has-icon icon-size="is-small">
          {{$t('change-ontology-warning-message')}}
        </b-message>
        <b-field :label="$t('ontology')">
          <b-select
            v-model="selectedOntology"
            :placeholder="$t('no-ontology')"
            :disabled="loadingOntologies"
            :loading="loadingOntologies"
          >
            <option :value="null">
              {{$t('no-ontology')}}
            </option>
            <option v-for="ontology in ontologies" :value="ontology.id" :key="ontology.id">
              {{ontology.name}}
            </option>
          </b-select>
        </b-field>
      </template>

      <template #footer>
        <button
          class="button"
          type="button"
          @click="isOntologyModalActive = false"
          :disabled="savingOntology"
        >
          {{$t('button-cancel')}}
        </button>
        <button
          v-if="!errorOntologies"
          class="button is-link"
          :class="{'is-loading': savingOntology}"
          :disabled="loadingOntologies || savingOntology"
        >
          {{$t('button-save')}}
        </button>
      </template>
    </ims-modal>
  </form>

  <!-- UI-9b: set/replace the project image — a URL or an uploaded image (stored inline as a
       data-URL). Persisted as a hidden project property; shown on the project card. -->
  <ims-modal :active="isImageModalActive" :title="$t('change-project-image')" @close="isImageModalActive = false">
    <b-tabs v-model="imageMode" size="is-small" expanded>
      <b-tab-item :label="$t('image-url')" value="url">
        <b-field>
          <b-input v-model="imageUrlInput" type="url" placeholder="https://..." expanded />
        </b-field>
      </b-tab-item>
      <b-tab-item :label="$t('upload')" value="upload">
        <b-upload v-model="imageFile" accept="image/*" drag-drop expanded @input="onImageFile">
          <section class="proj-img-drop">
            <p><i class="fas fa-upload fa-2x"></i></p>
            <p>{{ imageFile ? imageFile.name : $t('choose-file-or-drop') }}</p>
          </section>
        </b-upload>
      </b-tab-item>
    </b-tabs>
    <figure v-if="imagePreview" class="proj-img-preview">
      <img :src="imagePreview" alt="">
    </figure>

    <template #footer>
      <button class="button" type="button" :disabled="savingImage" @click="isImageModalActive = false">
        {{$t('button-cancel')}}
      </button>
      <button v-if="existingImageProp" class="button is-danger is-light" type="button"
              :disabled="savingImage" @click="removeProjectImage()">
        {{$t('remove-image')}}
      </button>
      <button class="button is-link" type="button" :class="{'is-loading': savingImage}"
              :disabled="!canSaveImage || savingImage" @click="saveProjectImage()">
        {{$t('button-save')}}
      </button>
    </template>
  </ims-modal>

  <div class="buttons">
    <button class="button" :class="size" @click="isRenameModalActive = true">
      {{$t('button-rename')}}
    </button>
    <button class="button" :class="size" @click="isOntologyModalActive = true" :disabled="cannotDeleteOntology">
      {{$t('button-change-ontology')}}
    </button>
    <button class="button" :class="size" @click="isImageModalActive = true">
      {{$t('change-project-image')}}
    </button>
    <button class="button is-danger" :class="size" @click="deleteProject()">
      {{$t('button-delete')}}
    </button>
  </div>
</div>
</template>

<script>
import {OntologyCollection, Property, PropertyCollection, AttachedFile} from '@/api';
import ImsModal from '@/components/utils/ImsModal';
import RenameModal from '@/components/utils/RenameModal';
import {PROJECT_IMAGE_KEY} from '@/utils/project-image.js';

export default {
  name: 'project-actions',
  props: {
    project: {type: Object},
    size: {type: String, default: 'is-small'}
  },
  components: {
    ImsModal,
    RenameModal
  },
  data() {
    return {
      isRenameModalActive: false,
      isOntologyModalActive: false,
      loadingOntologies: true,
      errorOntologies: false,
      ontologies: null,
      selectedOntology: null,
      cannotDeleteOntology: false,
      savingOntology: false,
      // UI-9b: project image modal state
      isImageModalActive: false,
      imageMode: 'url',
      imageUrlInput: '',
      imageFile: null,
      imagePreviewUrl: null,
      savingImage: false,
      existingImageProp: null
    };
  },
  watch: {
    async isOntologyModalActive(val) {
      if (val) {
        if (this.loadingOntologies) { // first opening of the ontology modal => load ontologies
          try {
            this.ontologies = (await OntologyCollection.fetchAll({light: true})).array;
            this.ontologies.sort((a, b) => a.name.localeCompare(b.name));
            this.loadingOntologies = false;
          } catch (error) {
            console.log(error);
            this.errorOntologies = true;
          }
        }

        // preselect the ontology of the project
        this.selectedOntology = this.project.ontology;
        this.savingOntology = false;
      }
    },
    async isImageModalActive(val) {
      if (val) {
        await this.loadProjectImage();
      }
    }
  },
  computed: {
    imagePreview() {
      return this.imageMode === 'upload' ? this.imagePreviewUrl : (this.imageUrlInput || null);
    },
    canSaveImage() {
      return this.imageMode === 'upload' ? !!this.imageFile : !!this.imageUrlInput;
    }
  },
  methods: {
    // UI-9b: load existing project image (if any) into the modal.
    async loadProjectImage() {
      this.imageUrlInput = '';
      this.imageFile = null;
      this.imagePreviewUrl = null;
      this.existingImageProp = null;
      this.imageMode = 'url';
      try {
        const props = (await PropertyCollection.fetchAll({object: this.project})).array;
        const prop = props.find(p => p.key === PROJECT_IMAGE_KEY);
        if (prop) {
          this.existingImageProp = prop;
          // a pasted URL → prefill the URL field; an uploaded file → show the Upload tab.
          if (prop.value && /^https?:\/\//i.test(prop.value)) {
            this.imageUrlInput = prop.value;
          } else if (prop.value) {
            this.imageMode = 'upload';
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    onImageFile(file) {
      if (this.imagePreviewUrl) {
        URL.revokeObjectURL(this.imagePreviewUrl);
      }
      this.imageFile = file || null;
      this.imagePreviewUrl = file ? URL.createObjectURL(file) : null;
    },
    async saveProjectImage() {
      this.savingImage = true;
      try {
        let value;
        if (this.imageMode === 'upload') {
          if (!this.imageFile) {
            this.savingImage = false;
            return;
          }
          // Store the actual image as an AttachedFile on the project (no size limit);
          // the property holds only its short relative download URL.
          const attached = await new AttachedFile(
            {file: this.imageFile, filename: this.imageFile.name}, this.project
          ).save();
          value = attached.url;
        } else {
          value = this.imageUrlInput.trim();
          if (!value) {
            this.savingImage = false;
            return;
          }
        }
        if (this.existingImageProp) {
          this.existingImageProp.value = value;
          await this.existingImageProp.save();
        } else {
          await new Property({object: this.project, key: PROJECT_IMAGE_KEY, value}).save();
        }
        this.isImageModalActive = false;
        this.$emit('update', this.project);
        // UI-9b: let any project card for this project refresh its image immediately (no reload).
        this.$eventBus.$emit('project-image-updated', {projectId: this.project.id, value});
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-save-prop')});
      } finally {
        this.savingImage = false;
      }
    },
    async removeProjectImage() {
      if (!this.existingImageProp) {
        return;
      }
      this.savingImage = true;
      try {
        await Property.delete(this.existingImageProp.id, this.project);
        this.isImageModalActive = false;
        this.$emit('update', this.project);
        this.$eventBus.$emit('project-image-updated', {projectId: this.project.id, value: null});
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-remove-prop')});
      } finally {
        this.savingImage = false;
      }
    },
    async rename(newName) {
      let updatedProject = this.project.clone();
      try {
        updatedProject.name = newName;
        await updatedProject.save();
        this.$emit('update', updatedProject);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-project-rename', {projectName: updatedProject.name})
        });
      } catch (error) {
        console.log(error);
        this.$notify({
          type: 'error',
          text: this.$t('notif-error-project-rename', {projectName: this.project.name})
        });
      }
      this.isRenameModalActive = false;
    },

    async saveOntology(forceOntologyUpdate = false) {
      this.savingOntology = true; // possibly long operation => give user visual indication that it is in progress
      let updatedProject = this.project.clone();
      try {
        updatedProject.ontology = this.selectedOntology;
        updatedProject.forceOntologyUpdate = forceOntologyUpdate; // to delete annotationterms if some exist
        await updatedProject.save();
        this.$emit('update', updatedProject);
        this.$notify({
          type: 'success',
          text: this.$t('notif-success-project-ontology-change', {projectName: this.project.name})
        });
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data.errorValues) {
          let counts = error.response.data.errorValues;

          // if the only terms associations are performed by users, ask confirmation and delete them
          if (counts.reviewedAssociatedTermsCount === 0) {
            this.$buefy.dialog.confirm({
              title: this.$t('confirm-ontology-change'),
              message: this.$t('confirm-ontology-change-delete-user-terms', {count: counts.userAssociatedTermsCount}),
              type: 'is-danger',
              confirmText: this.$t('button-confirm'),
              cancelText: this.$t('button-cancel'),
              onConfirm: () => this.saveOntology(true)
            });
          } else { // otherwise, backend does not allow automatic deletion => notify of error
            this.cannotDeleteOntology = true;
            this.$notify({
              type: 'error',
              text: this.$t('notif-error-project-ontology-change-reviewed-terms')
            });
          }
        } else {
          this.$notify({
            type: 'error',
            text: this.$t('notif-error-project-ontology-change', {projectName: this.project.name})
          });
        }
      }
      this.savingOntology = false;
      this.isOntologyModalActive = false;
    },

    deleteProject() {
      this.$buefy.dialog.confirm({
        title: this.$t('delete-project'),
        message: this.$t('delete-project-confirmation-message', {projectName: this.project.name}),
        type: 'is-danger',
        confirmText: this.$t('button-confirm'),
        cancelText: this.$t('button-cancel'),
        onConfirm: () => this.$emit('delete')
      });
    }
  }
};
</script>

<style scoped>
/* UI-9b: project image modal */
.proj-img-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  padding: 1.4rem;
  color: #5a6172;
}
.proj-img-warn { margin-top: 0.6rem; }
.proj-img-preview {
  margin-top: 0.9rem;
  border: 1px solid #e5e8ee;
  border-radius: 8px;
  overflow: hidden;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7f8fa;
}
.proj-img-preview img { max-width: 100%; max-height: 180px; object-fit: contain; }
</style>
